// Ayuda del operador — fuente única de verdad.
// Se usa desde GridEditor (Programación) y TemplateEditor (Plantilla): el contenido
// es el MISMO en ambos lados; solo cambia el color de acento vía la prop `accent`.

const ACCENTS = {
  orange: {
    header: "bg-orange-700",
    step: "bg-orange-100 text-orange-800 border-orange-300",
    button: "bg-orange-600 hover:bg-orange-700",
    heading: "text-orange-700",
  },
  emerald: {
    header: "bg-emerald-700",
    step: "bg-emerald-100 text-emerald-800 border-emerald-300",
    button: "bg-emerald-600 hover:bg-emerald-700",
    heading: "text-emerald-700",
  },
};

function Section({ title, headingClass, children }) {
  return (
    <section className="mb-6">
      <h3 className={`text-base font-bold mb-2 ${headingClass}`}>{title}</h3>
      <div className="text-sm text-gray-700 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

export default function HelpModal({ open, onClose, accent = "orange" }) {
  if (!open) return null;
  const c = ACCENTS[accent] || ACCENTS.orange;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1100] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-2xl shadow-2xl flex flex-col max-h-[85vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Encabezado */}
        <div className={`flex items-center justify-between px-6 py-4 rounded-t-lg ${c.header}`}>
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <span>❓</span> Cómo funciona el panel
          </h2>
          <button
            onClick={onClose}
            className="text-white/80 hover:text-white text-xl leading-none w-7 h-7 flex items-center justify-center rounded hover:bg-black/20"
            aria-label="Cerrar"
          >
            ✕
          </button>
        </div>

        {/* Cuerpo scrollable */}
        <div className="px-6 py-5 overflow-y-auto">
          <Section title="El flujo en 3 pasos" headingClass={c.heading}>
            <div className="grid gap-2">
              {[
                ["1", "Completá la plantilla", "Armás una semana modelo (lunes a domingo) con todos los días llenos."],
                ["2", "Se repite sola", "El sistema copia esa plantilla hacia las semanas siguientes que estén vacías."],
                ["3", "Ajustás la programación", "Retocás a mano lo que haga falta en cada semana; tus cambios se conservan."],
              ].map(([n, t, d]) => (
                <div key={n} className={`flex gap-3 items-start border rounded-md px-3 py-2 ${c.step}`}>
                  <span className="font-bold text-lg leading-none mt-0.5">{n}</span>
                  <div>
                    <div className="font-semibold">{t}</div>
                    <div className="text-xs opacity-90">{d}</div>
                  </div>
                </div>
              ))}
            </div>
          </Section>

          <Section title="1. Completá la plantilla primero" headingClass={c.heading}>
            <p>
              La <strong>plantilla</strong> es el punto de partida de todo. Es una semana modelo:
              siete días, de lunes a domingo, con la programación que se repite habitualmente.
            </p>
            <p>
              Cada día tiene que quedar <strong>completo</strong>: cubierto de{" "}
              <strong>00:00 a 00:00 sin huecos</strong> entre programas. En la barra lateral vas
              a ver el estado de cada día con <strong>✅</strong> (completo) o <strong>❌</strong>{" "}
              (incompleto).
            </p>
            <p>
              Hasta que los <strong>7 días</strong> no estén en ✅, el acceso a{" "}
              <strong>Programación</strong> queda bloqueado 🔒. Esto es a propósito: sin una
              plantilla completa, el sistema no tiene de dónde copiar.
            </p>
          </Section>

          <Section title="2. Cómo funciona la plantilla" headingClass={c.heading}>
            <p>
              Cada franja de la plantilla guarda su <strong>horario</strong>, su{" "}
              <strong>título</strong> y su <strong>descripción</strong>. Se edita igual que la
              programación: arrastrás para mover, estirás los bordes para cambiar la duración y
              hacés clic para editar el texto. La <strong>sinopsis admite hasta 250
              caracteres</strong> (vas a ver un contador al editarla).
            </p>
            <p>
              Cuando guardás una plantilla completa, el sistema la <strong>compara</strong> con
              las próximas 4 semanas ya cargadas y te muestra las <strong>diferencias</strong>.
              Ahí decidís:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Sobrescribir</strong>: reemplaza esas semanas con la plantilla nueva.
              </li>
              <li>
                <strong>Mantener</strong>: deja la programación actual como está y solo guarda la
                plantilla para las semanas futuras.
              </li>
            </ul>
          </Section>

          <Section title="3. Qué se repite automáticamente (y cómo)" headingClass={c.heading}>
            <p>
              Una vez que la plantilla está completa, hay <strong>dos momentos</strong> en que el
              sistema la copia hacia adelante:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Todos los días a las 5:00 de la mañana</strong>, el servidor revisa la
                semana siguiente y, si está vacía, la puebla con la plantilla, aunque nadie entre
                al panel.
              </li>
              <li>
                <strong>Al abrir el editor de Programación</strong>, se completan las próximas
                semanas que estén vacías, así siempre ves grilla cargada hacia adelante.
              </li>
            </ul>
            <p>
              En ambos casos vale la misma regla clave: <strong>solo se rellenan semanas
              vacías</strong>. Si una semana ya tiene programación cargada, el sistema{" "}
              <strong>no la toca</strong>. Por eso podés adelantar trabajo a mano sin miedo a que
              el relleno te pise los cambios.
            </p>
            <p className="text-xs text-gray-500">
              Nota: un canal cuya plantilla no esté completa no se rellena hasta que se completen
              sus 7 días.
            </p>
          </Section>

          <Section title="4. Cómo se replica en la programación" headingClass={c.heading}>
            <p>
              Al copiar la plantilla a una semana, <strong>cada franja va al mismo día y a la
              misma hora</strong>: lo que en la plantilla está el martes a las 20:00, aparece el
              martes a las 20:00 de la semana destino.
            </p>
            <p>Cuando aplicás la plantilla manualmente a una semana, elegís cómo:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Reemplazar la semana</strong>: borra lo que había y deja exactamente la
                plantilla.
              </li>
              <li>
                <strong>Completar huecos</strong>: mantiene lo que ya estaba y solo agrega la
                plantilla en los espacios vacíos.
              </li>
            </ul>
          </Section>

          <Section title="5. Cómo modificar la programación" headingClass={c.heading}>
            <p>
              En <strong>Programación</strong> trabajás sobre la grilla real de cada semana:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                <strong>Mover</strong>: arrastrá un bloque a otro horario.
              </li>
              <li>
                <strong>Cambiar duración</strong>: estirá o achicá desde los bordes del bloque.
              </li>
              <li>
                <strong>Editar</strong>: hacé clic para cambiar título y descripción.
              </li>
              <li>
                <strong>Guardar</strong>: usá el botón Guardar para que los cambios queden firmes.
              </li>
            </ul>
            <p>
              Tus ajustes manuales <strong>se conservan</strong>: el relleno automático salta las
              semanas que ya tienen datos. Si querés volver al modelo, siempre podés{" "}
              <strong>reaplicar la plantilla</strong> a una semana puntual.
            </p>
          </Section>

          <div className="rounded-md border border-gray-200 bg-gray-50 px-4 py-3 text-xs text-gray-600">
            <strong>En resumen:</strong> la plantilla es el molde, la programación es lo que sale
            en el aire. Primero armás el molde completo, el sistema lo repite hacia adelante en las
            semanas vacías, y vos ajustás a mano lo que cada semana necesite.
          </div>
        </div>

        {/* Pie */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className={`text-white px-5 py-2 rounded-md text-sm font-semibold ${c.button}`}
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
